/*
 * Shared primitive for every login surface that decides "Descope says this account must
 * complete an SMS challenge" — the legacy Member Hub login, the Descope-native login page,
 * and the SSO callback. Each caller still runs its own flag check / getSecurityStatus()
 * call (the enforcement rule and its fail-open/fail-closed nuances live there, unchanged);
 * this only centralises what happens once that decision is "yes, challenge required":
 * park the just-committed session and hand off to the shared challenge page.
 */
import useMfaLogging, { MFA_EVENT, MFA_CONTEXT } from '~/composables/use-mfa-logging'

export default function useMfaPostLoginGate () {
  const userStore = useUserStore()
  const { descopeAccessToken, descopeRefreshToken } = useDescopeSession()

  /*
   * Suspends the session in place of showing an inline OTP step, and records everything
   * the challenge page needs to send/verify the code and get the user to `destination`
   * afterwards.
   *
   * Must run BEFORE userStore.suspendAuthSession() clears `token` — descopeAccessToken can
   * fall back to reading `token` itself when the primary session IS Descope's (Descope-
   * native login, Descope OAuth), so without copying it into `descopeSession` first, the
   * challenge page would be left with no usable Descope credential once suspended.
   *
   * `phone` is Descope's own (already-verified) number from the securityStatus check that
   * triggered this — carried through so the challenge page can sync it back to Member Hub
   * once verified, the same as every other already-enrolled login already does.
   *
   * @param {{email: string, phone?: string, destination: {path: string, query?: object}|null}} params
   */
  function suspendForChallenge ({ email, phone, destination }) {
    if (!userStore.descopeSession?.refresh_token) {
      userStore.setDescopeSession({
        access_token: descopeAccessToken.value,
        refresh_token: descopeRefreshToken.value
      })
    }

    const suspended = userStore.suspendAuthSession()
    userStore.setPendingMfaChallenge({ suspended, email, phone: phone || '', destination: destination || null })
  }

  /*
   * Same idea as suspendForChallenge, for mandatory enrolment instead of verification: a
   * committed session must not stay live and usable while MFA enrolment — required, not
   * offered — is still outstanding. See pendingMfaEnrollment's own comment in state.js for
   * why this exists.
   *
   * allowSkip must be resolved by the CALLER from the still-live profile before calling
   * this — suspendAuthSession() below wipes userStore.userProfile, so setup-mfa.vue has
   * nothing left to read force_MFA from once suspended. It travels through the pending
   * record instead of being recomputed downstream. Defaults to false (mandatory) if the
   * caller omits it, matching this app's fail-closed convention.
   *
   * @param {{email: string, phone?: string, destination: {path: string, query?: object}|null, allowSkip?: boolean}} params
   */
  function suspendForEnrollment ({ email, phone, destination, allowSkip }) {
    if (!userStore.descopeSession?.refresh_token) {
      userStore.setDescopeSession({
        access_token: descopeAccessToken.value,
        refresh_token: descopeRefreshToken.value
      })
    }

    const suspended = userStore.suspendAuthSession()
    userStore.setPendingMfaEnrollment({ suspended, email, phone: phone || '', destination: destination || null, allowSkip: !!allowSkip })
  }

  /*
   * Lost-phone recovery entry point (login-time challenge, and the enrolment known-number
   * branch): suspends the primary session the same way suspendForChallenge does, but parks
   * it under pendingMfaRecovery instead. The session must stay suspended through BOTH
   * recovery steps (email OTP, then new-phone OTP) — see restoreFromRecovery, which is the
   * only thing allowed to bring it back.
   *
   * @param {{email: string, destination: {path: string, query?: object}|null}} params
   */
  function suspendForRecovery ({ email, destination }) {
    if (!userStore.descopeSession?.refresh_token) {
      userStore.setDescopeSession({
        access_token: descopeAccessToken.value,
        refresh_token: descopeRefreshToken.value
      })
    }

    const suspended = userStore.suspendAuthSession()
    userStore.setPendingMfaRecovery({ suspended, email, destination: destination || null })
  }

  /*
   * The ONLY call site allowed to bring back a session suspended by suspendForRecovery —
   * must only be invoked once the new phone number has actually verified (never on email-OTP
   * success alone; see MfaRecovery). Defensive no-op when there's nothing parked, so an
   * accidental double-call (or a call before suspension ever happened) can't restore an
   * empty/undefined session.
   *
   * @param {{suspended: object|null|undefined}} pending
   */
  function restoreFromRecovery (pending) {
    if (!pending?.suspended) return

    userStore.restoreAuthSession(pending.suspended)
    userStore.clearPendingMfaRecovery()
  }

  /*
   * "Can't access this number?" on the login-time SMS challenge — the session is ALREADY
   * suspended at that point (mfa-challenge.vue's own pendingMfaChallenge), so this carries
   * that same suspended payload into pendingMfaRecovery rather than calling
   * suspendAuthSession() again, which would suspend an already-empty session and lose the
   * real one.
   *
   * @param {{suspended: object, email: string, destination: object|null}} pending
   */
  function startRecoveryFromChallenge (pending) {
    /*
     * Logged here rather than on the recovery page's mount: this is the single funnel both
     * "Can't access this number?" entry points go through, and a reload of the recovery
     * page would otherwise re-log an entry that only happened once. The session is
     * suspended, so the token comes from the parked payload.
     */
    useMfaLogging().logMfaEvent(MFA_EVENT.RECOVERY_STARTED, {
      context: MFA_CONTEXT.RECOVERY_EMAIL,
      token: pending?.suspended?.token?.access_token,
      entry_point: 'sms_challenge'
    })

    // phone is the number being replaced — the recovery phone step shows it as "Current number".
    userStore.setPendingMfaRecovery({
      suspended: pending?.suspended || null,
      email: pending?.email || '',
      phone: pending?.phone || '',
      destination: pending?.destination || null
    })
    userStore.clearPendingMfaChallenge()
  }

  return { suspendForChallenge, suspendForEnrollment, suspendForRecovery, restoreFromRecovery, startRecoveryFromChallenge }
}
